import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ListItem, StepLabel, Tooltip, Typography, makeStyles } from '@ellucian/react-design-system/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { CONSTANTS } from '../Helpers/Configuration';
import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { Icon } from '@ellucian/ds-icons/lib';
import { colorTextAlertNeutral, colorTextError } from '@ellucian/react-design-system/core/styles/tokens';

const useStyles = makeStyles(() => ({
    defaultIcon: {
        color: colorTextAlertNeutral
    },
    listItem: {
        cursor: 'pointer',
    },
    containsErrors: {
        color: colorTextError
    }
}));

const selectImportAdminMode = state => state.settings.importAdminMode;

const ApplicationFormSideBarListItem = props => {
    const {
        active = false,
        onClick: onClickProp,
        page,
    } = props;
    const intl = useIntl();
    const classes = useStyles();
    const selectCompleted = useMemo(() => {
        return createSelector(
            state => state.application?.pageStatuses?.[page.id],
            value => value === CONSTANTS.applicationPageStatuses.COMPLETED
        );
    }, [page]);

    const isCompleted = useSelector(selectCompleted);   
    const selectHasErrors = useMemo(() => {
        return createSelector(
            state => state.form.meta.forceShowErrors[page.id],
            state => state.form.errors[page.id] || {},
            state => state.form.touched[page.id] || {},
            (forceShowError, errors, touched) => {
                if (!errors || Object.keys(errors).length === 0 && !forceShowError) {
                    return false;
                }
                // We want to show errors in the side bar item if `forceShowError` for the page is true
                // or there are errors on touched fields. Errors on untouched fields will not cause the side
                // item to display an error state.
                const touchedWithErrors = Object.keys(touched)
                .some(key => !!touched[key] && !!errors[key]);
                return !!forceShowError || touchedWithErrors;
            }
        );
    }, [page]);

    const hasErrors = useSelector(selectHasErrors);
    const isImportAdminMode = useSelector(selectImportAdminMode);

    const onClick = useCallback(() => {
        onClickProp && onClickProp(page.id);
    }, [onClickProp, page]);

    const iconValue = useMemo(() => {
        if (hasErrors || (isImportAdminMode && !isCompleted)) {
            return (
                <Tooltip title={intl.formatMessage({ id: 'applicationFormSidebarErrorsTooltip' })}>
                    <Icon large name={active ? 'warning-solid' : 'warning'} className={classes.containsErrors} />
                </Tooltip>
            );
        }
        if (active) {
            return (
                <Tooltip title={intl.formatMessage({ id: 'applicationFormSidebarEditingPageTooltip' })}>
                    <Icon large name="edit" className={classes.defaultIcon} />
                </Tooltip>
            );
        }
        if (isCompleted) {
            return (
                <Tooltip title={intl.formatMessage({ id: 'applicationFormSidebarCompletedTooltip' })}>
                    <Icon large name="check-circle" className={classes.defaultIcon} />
                </Tooltip>
            );
        }
        return (
            <Tooltip title={intl.formatMessage({ id: 'applicationFormSidebarNotStartedPageTooltip' })}>
                <Icon large name="circle" className={classes.defaultIcon} />
            </Tooltip>
        );
    }, [active, isCompleted, hasErrors, isImportAdminMode, classes]);

    return (
        <ListItem divider onClick={onClick} className={classes.listItem} role="menuitem">
            <StepLabel
                active={active}
                completed={isCompleted}
                disabled={false}
                icon={iconValue}
            >
                <Typography color={hasErrors  || (isImportAdminMode && !isCompleted) ? 'error' : 'textPrimary'}>
                    <FormattedMessage id={page.titleId} />
                </Typography>
            </StepLabel>
        </ListItem>
    );
};
ApplicationFormSideBarListItem.propTypes = {
    active: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    page: PropTypes.object.isRequired,
};

export default ApplicationFormSideBarListItem;
